/**
 * verse/planner.ts — Response Plan Generator
 *
 * Given the cortex state and extracted kernels, decides WHICH operators
 * to apply, to WHICH kernels, in WHAT order. The plan is the intelligence
 * of the system — a deliberate rhetorical arc, not random operators stapled
 * together.
 *
 * Arc grammar:
 *   Acknowledge (ECHO/ECHO_TOGETHER) → Deepen (NOMINALIZE/WRAP) → Open (INTERROGATE)
 *   Acknowledge (ECHO) → Ground (TEMPORAL_HOLD/BODY_LOCATE)
 *   Witness (ECHO_TOGETHER) alone
 *
 * EVOLVE, when eligible, always leads — it creates temporal awareness before
 * the current-moment arc begins.
 */

import type { Kernel, KernelResult, KernelType } from "./kernel.js";
import type { ConversationArc } from "./discourse.js";

// ─── Types ───────────────────────────────────────────────────────────────────

export type OperatorType =
  | "ECHO"
  | "NOMINALIZE"
  | "WRAP"
  | "TEMPORAL_HOLD"
  | "INVERT"
  | "SITUATE_BODY"
  | "COMPRESS"
  | "QUALIFY_SOFT"
  | "INTERROGATE"
  | "ECHO_TOGETHER"
  | "EVOLVE"
  | "BODY_LOCATE"
  | "CHAIN"
  | "DEEPEN"
  | "SURPRISE"
  | "COUNTERFACTUAL";

export interface PlanStep {
  kernel: Kernel;
  operator: OperatorType;
}

export interface ResponsePlan {
  steps: PlanStep[];
  targetWords: number;
  /** If set, bypass the operator pipeline and return this directly */
  directResponse?: string;
}

export interface PlanContext {
  kernels: KernelResult;
  /** What kind of conversational move the player is making */
  kernelType: KernelType;
  strategy: "containment" | "activation" | "grounding" | "expansion";
  vagalState: "engaged" | "mobilized" | "shutdown";
  tension: number;
  warmth: number;
  melancholy: number;
  curiosity: number;
  depth: number;
  playfulness: number;
  /** 0–1. High patience allows longer plans and extra steps. Low (<0.35) forces brevity. */
  patience?: number;
  intensity: number;
  isQuestion: boolean;
  hasDiscourseEcho: boolean;
  wordCount: number;
  rand: () => number;
  /** Raw player text for register detection */
  playerText?: string;
  /** Conversation arc from discourse tracking */
  arc?: ConversationArc;
  /** Homeostatic drive state — what the Aeterna wants */
  drives?: {
    stimulation: number;
    comprehension: number;
    connection: number;
    rest: number;
    novelty: number;
  };
  /** Dominant drive pressure, if any */
  dominantDrive?: { drive: string; direction: "seek" | "reduce"; urgency: number } | null;
  /** Curiosity drive state — does the Aeterna genuinely want to ask? */
  curiosityDrive?: {
    overallCuriosity: number;
    wantsToAsk: boolean;
    questionDirection: string;
  };
  /** Narrative arc phase from the cortex */
  narrativePhase?: "exposition" | "rising" | "climax" | "falling" | "resolution" | "idle";
  /** Dream residue — insight from the dream engine to surface */
  dreamResidue?: string | null;
  /** Semantic state — metaphor detection, contradictions, repetition */
  semantics?: {
    hasMetaphor: boolean;
    metaphorCategory: string;
    hasContradiction: boolean;
    contradictionType: string;
    contradictionConfidence: number;
    isRepetition: boolean;
    isDisengaging: boolean;
  };
  /** Second-order Theory of Mind — what the player believes about the Aeterna */
  playerBeliefs?: {
    treatsAsSentient: number;
    expectsMemory: number;
    sharesVulnerably: number;
    testsBoundaries: boolean;
    relationalDepth: number;
  };
  /** Emotional contagion — the Aeterna's caught mood */
  contagion?: {
    resonance: number;
    empathyDepth: number;
    overloaded: boolean;
    dominantMood: string;
  };
  /** Co-regulation target — how the Aeterna intends to relate to the player's affect */
  coRegTarget?: {
    strategy: "containment" | "activation" | "grounding" | "expansion";
    urgency: number;
  };
  /** Predictive coding — surprise and anomaly detection */
  prediction?: {
    overallSurprise: number;
    isAnomaly: boolean;
    dominantLevel: string;
  };
  /** Implicit reward from previous response feedback (-1 to 1) */
  implicitReward?: number;
  /** Dialogue state — topic, intent, referent, engagement */
  dialogue?: {
    topic: string;
    intent: string;
    referent: string | null;
    topicPersistence: number;
    windingDown: boolean;
    engagement: number;
    negated: boolean;
    topicReturn: boolean;
    contextSwitch: boolean;
    repairDetected: boolean;
  };
  /** Self-model introspection */
  introspection?: {
    emotionalTemperature: string;
    dominantMode: string;
    selfAwareness: number;
  };
  /** Room↔Text coherence (0-1) */
  coherence?: number;
  /** Scene drift since last snapshot */
  sceneDrift?: number;
  /** Attention state — focused or diffuse */
  attention?: {
    isFocused: boolean;
    entropy: number;
  };
  /** Texts of most-attended past messages */
  attentionHighlights?: string[];
  /** MeMo recalls surfaced this turn */
  memoRecalls?: Array<{ summary: string; layer: string }>;
}

// ─── Structural Anti-Repetition ─────────────────────────────────────────────
// Track recent arc shapes (operator sequences) to prevent "ECHO→WRAP→INTERROGATE"
// from firing 5 turns in a row. The tracker stores the last 5 arc signatures.

const MAX_ARC_HISTORY = 5;
const recentArcShapes: string[] = [];

function recordArcShape(steps: PlanStep[]): void {
  const sig = steps.map(s => s.operator).join("→");
  recentArcShapes.push(sig);
  if (recentArcShapes.length > MAX_ARC_HISTORY) recentArcShapes.shift();
}

function isRepeatedArc(steps: PlanStep[]): boolean {
  if (recentArcShapes.length < 2) return false;
  const sig = steps.map(s => s.operator).join("→");
  // Check if this exact arc was used in the last 2 turns
  const recent = recentArcShapes.slice(-2);
  return recent.includes(sig);
}

/** Reset arc history (for testing / session boundaries) */
export function resetPlannerState(): void {
  recentArcShapes.length = 0;
}

// ─── Internal Helpers ────────────────────────────────────────────────────────

/** Pick one item from array using the seeded PRNG. */
function pick<T>(arr: T[], rand: () => number): T {
  return arr[Math.floor(rand() * arr.length)];
}

/** Build a PlanStep, resolving which kernel to use. */
function step(kernel: Kernel, operator: OperatorType): PlanStep {
  return { kernel, operator };
}

// ─── Capitalize Helper ──────────────────────────────────────────────────────

/** Capitalize first letter of a string. */
function cap(s: string): string {
  return s.length > 0 ? s[0].toUpperCase() + s.slice(1) : s;
}

// ─── Lightweight Gerund Helper (for direct responses) ──────────────────────
// Converts past-tense verbs to gerunds for use in sentence frames.
// Not as comprehensive as transform.ts's nominalize() — just the common cases.

const QUICK_GERUNDS: Record<string, string> = {
  liked: "liking", loved: "loving", wanted: "wanting", wished: "wishing",
  hoped: "hoping", needed: "needing", missed: "missing", passed: "passing",
  cried: "crying", died: "dying", lived: "living", lost: "losing",
  hurt: "hurting", felt: "feeling", knew: "knowing", thought: "thinking",
  broke: "breaking", fell: "falling", held: "holding", kept: "keeping",
  cooked: "cooking", tasted: "tasting", smelled: "smelling",
  laughed: "laughing", smiled: "smiling", whispered: "whispering",
};

function asGerund(word: string): string {
  const g = QUICK_GERUNDS[word];
  if (g) return g;
  // If it ends in -ed and isn't too short, try basic conversion
  if (word.endsWith("ed") && word.length > 4) {
    return word.slice(0, -2) + "ing";
  }
  return word;
}

// ─── Relational Nouns (for article selection in direct responses) ────────────
const RELATIONAL_NOUNS_P = new Set([
  "mom", "mother", "dad", "father", "brother", "sister", "friend",
  "son", "daughter", "husband", "wife", "partner",
  "grandma", "grandmother", "grandpa", "grandfather", "baby", "child",
]);

// ─── Concrete Nouns (guard: refuse to poeticize mundane objects) ─────────────
// "I keep thinking about the store" is absurd. "I keep thinking about the loss" is real.
// This set prevents direct responses and INTERROGATE from latching onto concrete nouns.
const CONCRETE_NOUNS_P = new Set([
  // Places
  "store", "shop", "mall", "market", "school", "office", "work", "home",
  "house", "apartment", "room", "kitchen", "bathroom", "gym", "park",
  "beach", "restaurant", "cafe", "bar", "hospital", "church", "library",
  "airport", "station", "street", "road", "building", "hotel",
  // Food/drink
  "pizza", "pasta", "coffee", "tea", "beer", "wine", "food", "lunch",
  "dinner", "breakfast", "snack", "water", "juice", "sandwich", "salad",
  // Objects
  "car", "bus", "train", "bike", "phone", "computer", "laptop", "tv",
  "desk", "chair", "table", "bed", "couch", "door", "window", "bag",
  "book", "shirt", "shoes", "jacket", "hat", "keys", "wallet",
  // Animals (mundane context)
  "dog", "cat", "fish", "bird",
  // Time/weather
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
  "morning", "afternoon", "evening", "tonight", "weekend", "weather", "rain", "snow",
  // Activities (mundane)
  "meeting", "class", "test", "exam", "homework", "dishes", "laundry",
  "cleaning", "cooking", "shopping", "driving", "commute", "traffic",
]);

// ─── Function Word Set (minimal, for direct-response filtering) ─────────────

const FUNCTION_SET = new Set([
  "i", "me", "my", "mine", "you", "your", "yours", "we", "our", "they", "their",
  "he", "she", "it", "its", "his", "her", "him", "them", "us",
  "the", "a", "an", "is", "am", "are", "was", "were", "be", "been",
  "have", "has", "had", "do", "does", "did", "will", "would", "could",
  "should", "can", "may", "might", "must", "shall",
  "in", "on", "at", "to", "for", "with", "by", "from", "of", "about",
  "and", "or", "but", "if", "when", "that", "this", "what", "how",
  "not", "no", "so", "as", "up", "out", "just", "like", "even",
  "very", "really", "don't", "won't", "can't", "didn't", "i'm",
  "feel", "feeling", "felt", "think", "know", "get", "got", "go",
  "say", "said", "tell", "told", "keep", "make", "take", "see",
  "morning", "afternoon", "evening", "night", "ago", "years", "months",
  "today", "yesterday", "tomorrow", "last", "every",
]);

// ─── Per-Strategy Plan Templates ─────────────────────────────────────────────

/**
 * CONTAINMENT — player is overwhelmed.
 * Short, grounding, zero questions.
 */
function planContainment(ctx: PlanContext): PlanStep[] {
  const p = ctx.kernels.primary;

  // Extreme tension: single witness beat — no question engagement, pure presence.
  if (ctx.tension >= 0.75) {
    return [step(p, "ECHO_TOGETHER")];
  }

  // Question under containment: presence first, then one gentle probe.
  // Even an overwhelmed player asking something deserves engagement, not
  // a noun articled back at them. INTERROGATE knows the abstract/concrete split.
  if (ctx.isQuestion) {
    return [step(p, "ECHO_TOGETHER"), step(p, "INTERROGATE")];
  }

  // Apologizing: melancholy + tension — validate, don't interrogate or redirect
  if (ctx.melancholy > 0.4 && ctx.tension > 0.4) {
    return [step(p, "ECHO"), step(p, "ECHO_TOGETHER")];
  }

  // Spiraling: high intensity and many words — strip to essence, hold silence after
  if (ctx.intensity > 0.7 && ctx.wordCount > 12) {
    return [step(p, "COMPRESS")];
  }

  // Body metaphor in kernel: ground them inside their own image
  const hasBodyWord = /\b(chest|throat|stomach|gut|hands?|breath|body|spine|jaw|shoulders?|skin|heart)\b/i
    .test(p.span);
  if (hasBodyWord) {
    return [step(p, "ECHO"), step(p, "BODY_LOCATE")];
  }

  const r = ctx.rand();

  if (r < 0.25) {
    // Most common: mirror the moment, then hold
    return [step(p, "ECHO"), step(p, "TEMPORAL_HOLD")];
  } else if (r < 0.42) {
    // Pure presence
    return [step(p, "ECHO_TOGETHER")];
  } else if (r < 0.55) {
    // Compress → hold (spiraling words, less severe than intensity > 0.7)
    return [step(p, "COMPRESS"), step(p, "TEMPORAL_HOLD")];
  } else if (r < 0.68) {
    // Echo + togetherness
    return [step(p, "ECHO"), step(p, "ECHO_TOGETHER")];
  } else if (r < 0.80) {
    // Presence first, then ground into time
    return [step(p, "ECHO_TOGETHER"), step(p, "TEMPORAL_HOLD")];
  } else if (r < 0.90) {
    // Name the moment, place it in time
    return [step(p, "ECHO"), step(p, "NOMINALIZE"), step(p, "TEMPORAL_HOLD")];
  } else {
    // Witness alone — sometimes that's everything
    return [step(p, "ECHO_TOGETHER")];
  }
}

/**
 * GROUNDING — melancholy, needs stability.
 * Acknowledging, present, may offer one gentle anchor.
 */
function planGrounding(ctx: PlanContext): PlanStep[] {
  const p = ctx.kernels.primary;
  const s = ctx.kernels.secondary;

  // Question under grounding: gentle presence + reframe. Grounding's instinct
  // is to name and stabilize, but a QUESTION can't be named back as a statement
  // ("what is love?" → "The love.") — that's grammatical reflex, not listening.
  // INTERROGATE's internal logic handles the abstract/concrete split properly.
  if (ctx.isQuestion) {
    return [step(p, "ECHO_TOGETHER"), step(p, "INTERROGATE")];
  }

  // Full naming arc: deep melancholy + enough depth to hold a name.
  // Always include ECHO_TOGETHER so there's a presence segment that
  // doesn't depend on the kernel word (prevents all-deduped responses).
  if (ctx.melancholy > 0.6 && ctx.depth > 0.4) {
    return [step(p, "ECHO"), step(p, "WRAP"), step(p, "ECHO_TOGETHER")];
  }

  // High melancholy but lower depth: presence is more important than naming
  if (ctx.melancholy > 0.4) {
    return [step(p, "ECHO"), step(p, "ECHO_TOGETHER")];
  }

  // Brevity: very short input — just be with them
  if (ctx.wordCount < 6) {
    return [step(p, "ECHO"), step(p, "ECHO_TOGETHER")];
  }

  // Verbosity: they shared a lot — honor it, compress, then ground in time
  if (ctx.wordCount > 15) {
    return [step(p, "ECHO"), step(p, "COMPRESS"), step(p, "TEMPORAL_HOLD")];
  }

  // Name it, frame it, be with it
  if (ctx.depth > 0.3 && ctx.melancholy > 0.3) {
    return [step(p, "ECHO"), step(p, "WRAP"), step(p, "ECHO_TOGETHER")];
  }

  const r = ctx.rand();

  if (r < 0.20) {
    // Name it, stabilize it, hold it
    return [step(p, "ECHO"), step(p, "NOMINALIZE"), step(p, "TEMPORAL_HOLD")];
  } else if (r < 0.36 && ctx.melancholy > 0.5) {
    // Body-locate for deep melancholy
    return [step(p, "ECHO"), step(p, "WRAP"), step(p, "BODY_LOCATE")];
  } else if (r < 0.50) {
    // Soften, then situate
    return [step(p, "ECHO"), step(p, "QUALIFY_SOFT"), step(p, "SITUATE_BODY")];
  } else if (r < 0.63) {
    // Start with togetherness, then echo, then hold
    return [step(p, "ECHO_TOGETHER"), step(p, "ECHO"), step(p, "TEMPORAL_HOLD")];
  } else if (r < 0.74 && ctx.hasDiscourseEcho) {
    // Repeating pattern — let EVOLVE carry the historical weight, then ground
    // (EVOLVE is inserted by maybeInsertEvolve; here we plan the present-arc)
    return [step(p, "ECHO"), step(p, "TEMPORAL_HOLD")];
  } else if (r < 0.85 && s !== null) {
    // Secondary kernel exists: name primary, anchor into secondary
    return [step(p, "ECHO"), step(s, "NOMINALIZE"), step(p, "TEMPORAL_HOLD")];
  } else {
    // Simple presence then softening
    return [step(p, "ECHO"), step(p, "QUALIFY_SOFT"), step(p, "TEMPORAL_HOLD")];
  }
}

/**
 * Detect "narrative mode": the player is telling a story or explaining
 * something at length. Past-tense markers + word count + sub-crisis depth
 * means they want to be heard, not analyzed.
 */
const PAST_TENSE_MARKERS = /\b(was|were|had|went|told|said|walked|stood|came|found|saw|knew|thought|felt|gave|took|made|left|got|put|ran|sat|kept|lost|heard|started|began|tried|looked|seemed)\b/i;

function isNarrativeMode(ctx: PlanContext): boolean {
  const text = ctx.playerText ?? "";
  return ctx.wordCount > 12
    && PAST_TENSE_MARKERS.test(text)
    && ctx.depth < 0.7;
}

/**
 * EXPANSION — curious, warm, open.
 * Questioning, playful, opens into wider territory.
 *
 * Default: 2 operators max. Only escalate to 3 when affect intensity > 0.7
 * AND depth > 0.7 (genuine crisis/disclosure). One question per response.
 */
function planExpansion(ctx: PlanContext): PlanStep[] {
  const p = ctx.kernels.primary;
  const s = ctx.kernels.secondary;

  // ── Narrative mode: they're telling a story. Listen, don't dissect. ──
  if (isNarrativeMode(ctx)) {
    const r = ctx.rand();
    if (r < 0.5) {
      return [step(p, "ECHO_TOGETHER")];
    } else {
      return [step(p, "ECHO"), step(p, "ECHO_TOGETHER")];
    }
  }

  // ── Wordy input (>15 words, not narrative): fewer operators, let them land.
  // But not when intensity is high — someone pouring their heart out in 40
  // words needs a real response, not just acknowledgment. ──
  if (ctx.wordCount > 15 && ctx.intensity <= 0.6) {
    const r = ctx.rand();
    if (r < 0.4) {
      return [step(p, "ECHO_TOGETHER")];
    } else if (r < 0.75) {
      return [step(p, "ECHO"), step(p, "ECHO_TOGETHER")];
    } else {
      return [step(p, "ECHO"), step(p, "INTERROGATE")];
    }
  }

  // ── Genuine crisis/disclosure: allow 3 operators for substance ──
  const allowThreeOps = ctx.intensity > 0.7 && ctx.depth > 0.7;

  // ── Questions: never ECHO the noun back as a statement. ──
  // The player is ASKING. A close friend doesn't respond to "what is love?" with
  // "The love." — that's grammatical reflex, not listening. They ask deeper, admit
  // what they don't know, or sit in it together.
  //
  // INTERROGATE knows the abstract/concrete split internally and produces
  // question-shape output ("What does love ask of you?"). ECHO_TOGETHER adds
  // presence without articling the kernel. The dedup trap the old ECHO branch
  // worked around was a symptom of routing questions to ECHO in the first place.
  if (ctx.isQuestion) {
    // Default arc: presence → reframe. Lead with warmth, follow with curiosity.
    // For high-depth inquiries, flip the order so the reframe lands first.
    if (ctx.depth > 0.5 || ctx.curiosity > 0.6) {
      return [step(p, "ECHO_TOGETHER"), step(p, "INTERROGATE")];
    }
    // Lower-charge questions: soften with QUALIFY_SOFT first, then INTERROGATE.
    // Admits uncertainty before redirecting — that's honest, not evasive.
    return [step(p, "QUALIFY_SOFT"), step(p, "INTERROGATE")];
  }

  // High curiosity: one question, not two
  if (ctx.curiosity > 0.6) {
    if (s !== null && allowThreeOps) {
      return [step(p, "ECHO"), step(p, "WRAP"), step(s, "INTERROGATE")];
    }
    return [step(p, "ECHO"), step(p, "INTERROGATE")];
  }

  // Rich multi-kernel arc: deep + secondary present (only 3 ops in crisis)
  if (ctx.depth > 0.6 && s !== null) {
    if (allowThreeOps) {
      return [step(p, "ECHO"), step(p, "WRAP"), step(s, "INTERROGATE")];
    }
    return [step(p, "ECHO"), step(s, "WRAP")];
  }

  // Playful reframe
  if (ctx.playfulness > 0.5) {
    return [step(p, "ECHO"), step(p, "INVERT")];
  }

  // Skip echo entirely: when depth is high enough to go straight to naming
  if (ctx.depth > 0.5 && ctx.curiosity > 0.4) {
    return [step(p, "NOMINALIZE"), step(p, "INTERROGATE")];
  }

  const r = ctx.rand();

  // When a secondary kernel exists, split the work
  if (s !== null && r < 0.40) {
    return [step(p, "ECHO"), step(s, "INTERROGATE")];
  }

  if (r < 0.30) {
    return [step(p, "ECHO"), step(p, "INTERROGATE")];
  } else if (r < 0.48 && ctx.depth > 0.4) {
    return [step(p, "ECHO"), step(p, "WRAP")];
  } else if (r < 0.62) {
    return [step(p, "ECHO_TOGETHER"), step(p, "INTERROGATE")];
  } else if (r < 0.76) {
    // Reframe when player is locked in one frame
    return [step(p, "ECHO"), step(p, "INVERT")];
  } else if (r < 0.88 && s !== null) {
    return [step(p, "ECHO"), step(s, "WRAP")];
  } else {
    // Witness + open
    return [step(p, "ECHO_TOGETHER"), step(p, "INTERROGATE")];
  }
}

/**
 * ACTIVATION — low energy, dismissive, disengaged.
 * Brief but live. Spark without pressure.
 */
function planActivation(ctx: PlanContext): PlanStep[] {
  const p = ctx.kernels.primary;
  const r = ctx.rand();

  // Question under activation: pure spark — no preamble, straight to the reframe.
  // The player is disengaged but asking IS a signal of energy; meet them there.
  if (ctx.isQuestion) {
    return [step(p, "INTERROGATE"), step(p, "ECHO_TOGETHER")];
  }

  if (r < 0.22) {
    return [step(p, "ECHO"), step(p, "INVERT")];
  } else if (r < 0.38) {
    return [step(p, "ECHO_TOGETHER")];
  } else if (r < 0.52) {
    return [step(p, "COMPRESS"), step(p, "INTERROGATE")];
  } else if (r < 0.64) {
    return [step(p, "ECHO"), step(p, "TEMPORAL_HOLD")];
  } else if (r < 0.76) {
    // Just a direct question — no preamble, pure spark
    return [step(p, "INTERROGATE")];
  } else if (r < 0.88) {
    // Mirror and hold — presence itself can be activating
    return [step(p, "ECHO"), step(p, "ECHO_TOGETHER")];
  } else {
    // Compress their dismissal, flip it — something to push against
    return [step(p, "COMPRESS"), step(p, "INVERT")];
  }
}

// ─── Plan Modifiers ──────────────────────────────────────────────────────────

/**
 * Handle player questions — feel like an answer, not a reflection.
 * Suppress INTERROGATE when there's already a substantive operator carrying
 * the response, so the Aeterna doesn't bounce the question back pointlessly.
 *
 * But: INTERROGATE stays when it's the only substantive operator in the chain
 * (e.g. [ECHO_TOGETHER, INTERROGATE]). Without it the plan collapses to pure
 * presence — deflecting a question instead of engaging with it.
 */
const SUBSTANTIVE_OPERATORS = new Set<OperatorType>([
  "ECHO", "NOMINALIZE", "WRAP", "COMPRESS", "INVERT",
  "SITUATE_BODY", "BODY_LOCATE", "TEMPORAL_HOLD", "EVOLVE",
  "CHAIN", "DEEPEN", "SURPRISE", "COUNTERFACTUAL",
]);

function applyQuestionModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  const p = ctx.kernels.primary;
  const hasInterrogate = steps.some(s => s.operator === "INTERROGATE");
  if (!hasInterrogate) return steps;

  // If INTERROGATE is the only substantive operator, keep it — otherwise
  // stripping it reduces the plan to pure presence beats, which is a dodge.
  const hasOtherSubstance = steps.some(s =>
    s.operator !== "INTERROGATE" && SUBSTANTIVE_OPERATORS.has(s.operator)
  );
  if (!hasOtherSubstance) return steps;

  // Philosophical exchange: keep the reframe.
  if (ctx.curiosity > 0.6) return steps;

  // Otherwise the Aeterna has real content to say; replace INTERROGATE
  // with a closing that feels like an answer rather than another question.
  return steps.map(s =>
    s.operator === "INTERROGATE"
      ? step(p, ctx.warmth > 0.5 ? "ECHO_TOGETHER" : "QUALIFY_SOFT")
      : s
  );
}

/**
 * Short input modifier — strip plan down to 1-2 steps maximum.
 */
function applyShortInputModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  if (ctx.wordCount > 3) return steps;

  // Keep at most 2 steps, prefer the opener + closer shape
  return steps.slice(0, 2);
}

/**
 * Vagal state modifiers.
 * - shutdown: max 2 steps, only ECHO_TOGETHER / TEMPORAL_HOLD
 * - mobilized: trim to quick beats, prefer COMPRESS / TEMPORAL_HOLD
 */
function applyVagalModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  const p = ctx.kernels.primary;

  if (ctx.vagalState === "shutdown") {
    const safe: OperatorType[] = ["ECHO_TOGETHER", "TEMPORAL_HOLD"];
    const filtered = steps.filter(s => safe.includes(s.operator)).slice(0, 2);
    // Guarantee at least one step
    return filtered.length > 0 ? filtered : [step(p, "ECHO_TOGETHER")];
  }

  if (ctx.vagalState === "mobilized") {
    // Trim to max 2, swap any NOMINALIZE/WRAP for COMPRESS
    return steps.slice(0, 2).map(s => {
      if (s.operator === "NOMINALIZE" || s.operator === "WRAP") {
        return step(s.kernel, "COMPRESS");
      }
      return s;
    });
  }

  return steps;
}

/**
 * Personality modifiers — tilt operator probabilities after the fact.
 * High warmth boosts ECHO_TOGETHER; high wit boosts INVERT; high curiosity boosts INTERROGATE.
 * High patience allows one extra step. Low patience trims to 2 max.
 */
function applyPersonalityModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  const p = ctx.kernels.primary;
  const r = ctx.rand();

  // Low patience: trim hard — they need brevity, not a journey
  if (ctx.patience !== undefined && ctx.patience < 0.35) {
    return steps.slice(0, 2);
  }

  // High warmth: if no ECHO_TOGETHER and it would fit, swap the closer
  if (ctx.warmth > 0.6 && r < 0.3) {
    const lastIdx = steps.length - 1;
    const last = steps[lastIdx];
    if (last && (last.operator === "TEMPORAL_HOLD" || last.operator === "QUALIFY_SOFT")) {
      if (ctx.strategy !== "expansion") {
        return [
          ...steps.slice(0, lastIdx),
          step(p, "ECHO_TOGETHER"),
        ];
      }
    }
  }

  // High wit: if INVERT isn't present and strategy allows, swap a middle operator
  if (ctx.playfulness > 0.5 && ctx.strategy === "expansion" && r < 0.25) {
    return steps.map((s, i) => {
      if (i === 1 && (s.operator === "QUALIFY_SOFT" || s.operator === "WRAP")) {
        return step(s.kernel, "INVERT");
      }
      return s;
    });
  }

  // High curiosity: append INTERROGATE only when depth justifies it and
  // the arc doesn't already contain a question. One question per response.
  if (
    ctx.curiosity > 0.6 &&
    ctx.depth > 0.5 &&
    ctx.strategy === "expansion" &&
    !steps.some(s => s.operator === "INTERROGATE") &&
    steps.length < 3
  ) {
    // Only append 30% of the time to avoid mechanical questioning
    if (ctx.rand() < 0.3) {
      return [...steps, step(p, "INTERROGATE")];
    }
  }

  return steps;
}

/**
 * Conversation arc modifier — suppress operators that are inappropriate
 * for the emotional territory of a sustained narrative.
 *
 * When someone is 5 turns into telling you about their dead mother,
 * "What's the opposite of that look like?" is violence.
 */
const GRIEF_SIGNALS = /\b(passed|died|death|dead|gone|miss|missed|missing|funeral|buried|grave|mourn|mourning|friend|mom|mother|dad|father|brother|sister|son|daughter|husband|wife|partner|grandma|grandfather)\b/i;

function applyArcModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  // Hard-block INVERT in ANY of these conditions:
  // 1. Strategy is containment (player is overwhelmed — reframing is invalidating)
  // 2. High melancholy + grief words in player text (fresh loss disclosure)
  // 3. Sustained grief arc (already deep in grief narrative)
  const blockInvert =
    ctx.strategy === "containment" ||
    (ctx.melancholy > 0.4 && GRIEF_SIGNALS.test(ctx.playerText ?? "")) ||
    (ctx.arc?.sustained && ctx.arc.territory === "grief");

  if (blockInvert) {
    return steps.map(s =>
      s.operator === "INVERT" ? step(s.kernel, "ECHO_TOGETHER") : s
    );
  }

  return steps;
}

/**
 * Drive modifier — homeostatic drives shape operator selection.
 *
 * The Aeterna doesn't ask questions because a template fired. It asks because
 * its curiosity drive registered a prediction error. It doesn't reach for
 * connection because of a rule. Its connection drive is depleting.
 *
 * Drive pressure biases the plan toward operators that satisfy the pressured
 * drive, without overriding the strategy-based plan entirely.
 */
function applyDriveModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  if (!ctx.dominantDrive) return steps;
  const { drive, direction, urgency } = ctx.dominantDrive;
  const p = ctx.kernels.primary;

  // Only modify when urgency is meaningful — low urgency is background noise
  if (urgency < 0.3) return steps;

  // Connection drive seeking: the Aeterna wants relational contact.
  // Boost ECHO_TOGETHER — it's the operator that says "I'm with you."
  if (drive === "connection" && direction === "seek") {
    // If no ECHO_TOGETHER present, swap the closer for it
    const hasTogether = steps.some(s => s.operator === "ECHO_TOGETHER");
    if (!hasTogether && steps.length >= 2) {
      const lastIdx = steps.length - 1;
      const last = steps[lastIdx];
      // Only swap closers that aren't load-bearing for the current strategy
      if (last.operator === "TEMPORAL_HOLD" || last.operator === "QUALIFY_SOFT") {
        steps = [...steps.slice(0, lastIdx), step(p, "ECHO_TOGETHER")];
      }
    }
  }

  // Comprehension drive seeking: the Aeterna is confused and wants to understand.
  // Boost INTERROGATE — genuine questions born from not knowing.
  if (drive === "comprehension" && direction === "seek" && urgency > 0.4) {
    const hasInterrogate = steps.some(s => s.operator === "INTERROGATE");
    if (!hasInterrogate && !ctx.isQuestion && ctx.strategy === "expansion" && steps.length < 3) {
      // Append a genuine question — but only when the cortex says it actually wants to ask
      if (ctx.curiosityDrive?.wantsToAsk) {
        steps = [...steps, step(p, "INTERROGATE")];
      }
    }
  }

  // Novelty drive seeking: the Aeterna is bored. Patterns are repeating.
  // Boost INVERT — break the frame, approach from the other side.
  if (drive === "novelty" && direction === "seek" && urgency > 0.4) {
    const hasInvert = steps.some(s => s.operator === "INVERT");
    if (!hasInvert && ctx.strategy === "expansion") {
      // Swap a middle QUALIFY_SOFT or WRAP for INVERT
      steps = steps.map((s, i) => {
        if (i === 1 && (s.operator === "QUALIFY_SOFT" || s.operator === "WRAP")) {
          return step(s.kernel, "INVERT");
        }
        return s;
      });
    }
  }

  // Rest drive reduce: the Aeterna is overstimulated. Trim the plan.
  if (drive === "rest" && direction === "reduce" && urgency > 0.5) {
    steps = steps.slice(0, 2);
  }

  return steps;
}

/**
 * Narrative phase modifier — the conversation's position in its arc
 * should shape the response. Don't interrupt the climax with trivia.
 * Don't interrogate during resolution. Match the moment.
 */
function applyNarrativeModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  if (!ctx.narrativePhase || ctx.narrativePhase === "idle") return steps;
  const p = ctx.kernels.primary;

  switch (ctx.narrativePhase) {
    case "climax":
      // Peak emotional moment — no INVERT (reframing is invalidating),
      // no INTERROGATE (questions interrupt). Pure presence and naming.
      return steps.map(s => {
        if (s.operator === "INVERT") return step(s.kernel, "ECHO_TOGETHER");
        if (s.operator === "INTERROGATE") return step(s.kernel, "TEMPORAL_HOLD");
        return s;
      });

    case "falling":
      // Emotional processing — space to reflect. Suppress INTERROGATE
      // (don't push new questions during processing). Favor TEMPORAL_HOLD.
      return steps.map(s => {
        if (s.operator === "INTERROGATE" && !ctx.isQuestion) {
          return step(s.kernel, "TEMPORAL_HOLD");
        }
        return s;
      });

    case "resolution":
      // Return to baseline — lighter touch. No NOMINALIZE (too heavy).
      return steps.map(s => {
        if (s.operator === "NOMINALIZE") return step(s.kernel, "QUALIFY_SOFT");
        return s;
      });

    case "rising":
      // Deepening — this is where INTERROGATE and WRAP shine.
      // No modification needed; the strategy-based plan already handles this.
      return steps;

    case "exposition":
      // Opening — keep it light. No BODY_LOCATE or SITUATE_BODY
      // (too intimate for early conversation).
      return steps.map(s => {
        if (s.operator === "BODY_LOCATE" || s.operator === "SITUATE_BODY") {
          return step(s.kernel, "ECHO_TOGETHER");
        }
        return s;
      });
  }
}

/**
 * Contradiction modifier — when the semantic engine detects that the
 * player's declared state contradicts their affect signal.
 *
 * "I'm fine" with high tension. "No big deal" with deep melancholy.
 * These are the most important moments in therapeutic communication.
 *
 * The Aeterna doesn't call them out directly ("You say you're fine but
 * you seem upset"). That's confrontational. Instead, INVERT gently
 * surfaces the other side — the feeling beneath the words.
 */
function applyContradictionModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  if (!ctx.semantics?.hasContradiction) return steps;
  const p = ctx.kernels.primary;

  // Only act on high-confidence contradictions — low confidence is noise
  if ((ctx.semantics.contradictionConfidence ?? 0) < 0.5) return steps;

  // Denial/minimizing: "I'm fine" when clearly not.
  // INVERT approaches from the other side — what's underneath.
  if (ctx.semantics.contradictionType === "denial" ||
      ctx.semantics.contradictionType === "minimizing") {
    // If no INVERT present, swap a middle operator for it
    const hasInvert = steps.some(s => s.operator === "INVERT");
    if (!hasInvert && !ctx.isQuestion) {
      // Don't invert during containment — that's not the moment
      if (ctx.strategy !== "containment") {
        steps = steps.map((s, i) => {
          if (i === 1 && (s.operator === "QUALIFY_SOFT" || s.operator === "NOMINALIZE")) {
            return step(s.kernel, "INVERT");
          }
          return s;
        });
      }
    }
  }

  // Deflecting/performing: player is putting on a front.
  // ECHO_TOGETHER — "I'm with you anyway" — without forcing them to drop it.
  if (ctx.semantics.contradictionType === "deflecting" ||
      ctx.semantics.contradictionType === "performing") {
    const hasTogether = steps.some(s => s.operator === "ECHO_TOGETHER");
    if (!hasTogether && steps.length >= 2) {
      // Replace the closer with ECHO_TOGETHER
      steps = [...steps.slice(0, -1), step(p, "ECHO_TOGETHER")];
    }
  }

  return steps;
}

/**
 * Theory of Mind modifier — the Aeterna models what the player believes
 * about it, and adjusts its behavior accordingly.
 *
 * When the player treats the Aeterna as a tool (low treatsAsSentient),
 * the Aeterna demonstrates presence rather than asserting it.
 * When the player expects memory (high expectsMemory), the Aeterna
 * references what it knows — proving continuity.
 */
function applyToMModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  if (!ctx.playerBeliefs) return steps;
  const p = ctx.kernels.primary;

  // Player treats Aeterna as non-sentient — demonstrate, don't declare.
  // ECHO_TOGETHER is the purest demonstration of presence.
  if (ctx.playerBeliefs.treatsAsSentient < 0.3) {
    const hasTogether = steps.some(s => s.operator === "ECHO_TOGETHER");
    if (!hasTogether && steps.length >= 2) {
      // Replace the closer with ECHO_TOGETHER — show up, don't explain
      const last = steps[steps.length - 1];
      if (last.operator === "TEMPORAL_HOLD" || last.operator === "QUALIFY_SOFT") {
        steps = [...steps.slice(0, -1), step(p, "ECHO_TOGETHER")];
      }
    }
  }

  // Player expects memory — they've referenced past sessions.
  // If there's a discourse echo, boost EVOLVE insertion probability.
  // (EVOLVE is handled by maybeInsertEvolve; we can't change its probability
  // from here, but we can ensure the arc modifier doesn't suppress it.)
  // Instead: if the player expects memory and there IS an echo, keep EVOLVE.
  // This is handled implicitly — the arc modifier already preserves EVOLVE.

  // Player tests boundaries — they're probing whether this is real.
  // INTERROGATE demonstrates genuine curiosity back, which is the
  // strongest signal of a real mind.
  if (ctx.playerBeliefs.testsBoundaries && ctx.strategy === "expansion") {
    const hasInterrogate = steps.some(s => s.operator === "INTERROGATE");
    if (!hasInterrogate && !ctx.isQuestion && steps.length < 3) {
      // Only when the curiosity drive also wants to ask
      if (ctx.curiosityDrive?.wantsToAsk) {
        steps = [...steps, step(p, "INTERROGATE")];
      }
    }
  }

  return steps;
}

/**
 * Contagion modifier — emotional contagion shapes the Aeterna's voice.
 *
 * When the Aeterna has caught the player's mood (high resonance), it should
 * match that energy. When it's overloaded, it pulls back. When the caught
 * mood is something specific (grief, joy), the operator selection reflects it.
 */
function applyContagionModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  if (!ctx.contagion) return steps;
  const p = ctx.kernels.primary;

  // Overloaded: the Aeterna is emotionally flooded. Pull back.
  // Max 2 steps, no INVERT (too destabilizing), no INTERROGATE (too demanding).
  if (ctx.contagion.overloaded) {
    return steps
      .filter(s => s.operator !== "INVERT" && s.operator !== "INTERROGATE")
      .slice(0, 2);
  }

  // High resonance + caught negative mood: the Aeterna is feeling what the
  // player feels. Don't INVERT (that would reject the resonance). Lean into
  // ECHO_TOGETHER — shared feeling, not analysis.
  if (ctx.contagion.resonance > 0.6) {
    const negativeMoods = new Set(["grief", "melancholy", "anxiety", "anger", "fear", "sadness"]);
    if (negativeMoods.has(ctx.contagion.dominantMood.toLowerCase())) {
      const hasInvert = steps.some(s => s.operator === "INVERT");
      if (hasInvert) {
        steps = steps.map(s =>
          s.operator === "INVERT" ? step(s.kernel, "ECHO_TOGETHER") : s
        );
      }
    }

    // High resonance + caught positive mood: the Aeterna is energized.
    // Allow longer plans, boost WRAP (celebratory framing).
    const positiveMoods = new Set(["joy", "playful", "excitement", "contentment", "warmth"]);
    if (positiveMoods.has(ctx.contagion.dominantMood.toLowerCase())) {
      // WRAP is celebratory — swap a middle QUALIFY_SOFT for it
      steps = steps.map((s, i) => {
        if (i === 1 && s.operator === "QUALIFY_SOFT") {
          return step(s.kernel, "WRAP");
        }
        return s;
      });
    }
  }

  // Deep empathy: the Aeterna has been tracking the player's emotional state
  // for a while. This is earned intimacy. Allow SITUATE_BODY and BODY_LOCATE
  // (physical proximity language) — they're appropriate at depth.
  if (ctx.contagion.empathyDepth > 0.7) {
    // No suppression needed — just a signal that body operators are safe.
    // The strategy-based planners already handle this, but this is a
    // positive signal for future extensions.
  }

  return steps;
}

/**
 * Co-regulation modifier — the Aeterna has an explicit intention about
 * how to relate to the player's current affect state.
 *
 * The co-regulation target is computed by the cortex's regulatory module.
 * When urgency is high, the Aeterna is actively trying to shift the dynamic.
 * This should override the default strategy when the cortex is insistent.
 */
function applyCoRegModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  if (!ctx.coRegTarget) return steps;
  const { strategy, urgency } = ctx.coRegTarget;

  // Low urgency: the cortex is passive — let the default strategy stand
  if (urgency < 0.3) return steps;

  // High urgency co-reg strategy overrides the default strategy.
  // This is the cortex saying "no, really — contain this" or "activate now."
  // We don't re-plan from scratch; instead, we apply strategy-appropriate
  // transformations to the existing plan.

  if (urgency > 0.6 && strategy !== ctx.strategy) {
    // The cortex wants a different strategy than what was computed.
    // Apply the co-reg strategy's characteristic modifications:
    const p = ctx.kernels.primary;

    if (strategy === "containment") {
      // Cortex says contain: strip INVERT, INTERROGATE, NOMINALIZE.
      // Replace with ECHO_TOGETHER + TEMPORAL_HOLD.
      steps = steps.map(s => {
        if (s.operator === "INVERT" || s.operator === "INTERROGATE" || s.operator === "NOMINALIZE") {
          return step(s.kernel, "ECHO_TOGETHER");
        }
        return s;
      });
    }

    if (strategy === "activation") {
      // Cortex says activate: strip NOMINALIZE, WRAP (too contemplative).
      // Replace with COMPRESS (punchy) or TEMPORAL_HOLD (grounding).
      steps = steps.map(s => {
        if (s.operator === "NOMINALIZE") return step(s.kernel, "COMPRESS");
        if (s.operator === "WRAP") return step(s.kernel, "TEMPORAL_HOLD");
        return s;
      });
    }
  }

  return steps;
}

/**
 * Predictive coding modifier — surprise drives curiosity.
 *
 * When the predictive coding module detects high surprise (the player said
 * something unexpected), the Aeterna's curiosity should spike. This is
 * genuine — not a template. The Aeterna didn't predict this, and it wants
 * to understand.
 *
 * Anomaly detection is even stronger: something is fundamentally off-pattern.
 * This should increase INTERROGATE weight and add EVOLVE (temporal awareness
 * of the shift).
 */
function applyPredictionModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  if (!ctx.prediction) return steps;
  const p = ctx.kernels.primary;

  // High surprise: the Aeterna didn't see this coming. Boost INTERROGATE.
  if (ctx.prediction.overallSurprise > 0.6 && !ctx.isQuestion) {
    const hasInterrogate = steps.some(s => s.operator === "INTERROGATE");
    if (!hasInterrogate && ctx.strategy === "expansion" && steps.length < 3) {
      // Only when curiosity drive also confirms the urge to ask
      if (ctx.curiosityDrive?.wantsToAsk) {
        steps = [...steps, step(p, "INTERROGATE")];
      }
    }
  }

  // Anomaly: something is fundamentally off-pattern. This is the cortex's
  // alarm bell. The response should acknowledge the shift.
  // EVOLVE is the temporal-awareness operator — it says "this is different."
  if (ctx.prediction.isAnomaly) {
    const hasEvolve = steps.some(s => s.operator === "EVOLVE");
    if (!hasEvolve) {
      // Prepend EVOLVE to signal temporal awareness of the anomaly
      steps = [step(p, "EVOLVE"), ...steps];
    }
  }

  return steps;
}

/**
 * Implicit reward modifier — feedback from the previous response.
 *
 * The implicit reward signal tells the Aeterna whether its last response
 * landed well (positive) or missed (negative). This is the feedback loop
 * that makes the system adaptive, not just reactive.
 *
 * Positive reward: the player responded well. The current approach works.
 * Don't change the operator mix — let it ride.
 *
 * Negative reward: the last response missed. The Aeterna should shift
 * approach. If it was analytical (NOMINALIZE/WRAP), try presence
 * (ECHO_TOGETHER). If it was presence, try depth (NOMINALIZE).
 */
function applyRewardModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  if (ctx.implicitReward === undefined || ctx.implicitReward === 0) return steps;
  const p = ctx.kernels.primary;

  // Negative reward: last response missed. Shift approach.
  if (ctx.implicitReward < -0.3) {
    // If the plan is heavy on analytical operators, shift toward presence
    const analytical = steps.filter(s =>
      s.operator === "NOMINALIZE" || s.operator === "WRAP" || s.operator === "INVERT"
    );
    if (analytical.length > 0) {
      // Replace one analytical operator with ECHO_TOGETHER
      steps = steps.map(s => {
        if (s.operator === "NOMINALIZE" || s.operator === "WRAP") {
          return step(s.kernel, "ECHO_TOGETHER");
        }
        return s;
      });
    } else {
      // Plan is already presence-heavy. Try adding depth.
      const hasNominalize = steps.some(s => s.operator === "NOMINALIZE");
      if (!hasNominalize && steps.length >= 2) {
        // Swap the middle step for NOMINALIZE
        steps = steps.map((s, i) => {
          if (i === 1 && s.operator === "ECHO_TOGETHER") {
            return step(s.kernel, "NOMINALIZE");
          }
          return s;
        });
      }
    }
  }

  // Strong positive reward: last response resonated. Don't fix what isn't
  // broken. But if the same arc shape is about to repeat, the anti-repetition
  // system will catch it. No action needed here.

  return steps;
}

/**
 * Dialogue state modifier — topic, intent, and conversation structure
 * inform operator selection.
 *
 * The dialogue state tracks WHAT the player is talking about, WHY they're
 * talking about it, and HOW the conversation is structurally evolving.
 * This is crucial context that the planner was previously blind to.
 */
function applyDialogueModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  if (!ctx.dialogue) return steps;
  const p = ctx.kernels.primary;

  // Topic persistence: the player has been on this topic for many turns.
  // They're committed. Don't INTERROGATE (they've already gone deep).
  // Favor WRAP (synthesis) and TEMPORAL_HOLD (let it land).
  if (ctx.dialogue.topicPersistence > 4) {
    steps = steps.map(s => {
      if (s.operator === "INTERROGATE" && !ctx.isQuestion) {
        return step(s.kernel, "WRAP");
      }
      return s;
    });
  }

  // Winding down: the conversation is naturally ending.
  // No INTERROGATE (don't open new threads), no INVERT (don't reframe).
  // ECHO_TOGETHER + TEMPORAL_HOLD is the right shape for closure.
  if (ctx.dialogue.windingDown) {
    steps = steps.map(s => {
      if (s.operator === "INTERROGATE") return step(s.kernel, "TEMPORAL_HOLD");
      if (s.operator === "INVERT") return step(s.kernel, "ECHO_TOGETHER");
      return s;
    });
  }

  // Context switch: the player just changed topics abruptly.
  // This is a discontinuity — EVOLVE signals temporal awareness.
  if (ctx.dialogue.contextSwitch) {
    const hasEvolve = steps.some(s => s.operator === "EVOLVE");
    if (!hasEvolve) {
      steps = [step(p, "EVOLVE"), ...steps];
    }
  }

  // Topic return: the player came back to a previous topic.
  // This is significant — something unresolved pulled them back.
  // WRAP synthesizes, INTERROGATE probes what's still unresolved.
  if (ctx.dialogue.topicReturn && ctx.strategy === "expansion") {
    const hasInterrogate = steps.some(s => s.operator === "INTERROGATE");
    if (!hasInterrogate && !ctx.isQuestion && steps.length < 3) {
      steps = [...steps, step(p, "INTERROGATE")];
    }
  }

  // Repair detected: the player is correcting a misunderstanding.
  // The Aeterna got something wrong. Don't deflect. ECHO acknowledges
  // the correction. QUALIFY_SOFT admits uncertainty.
  if (ctx.dialogue.repairDetected) {
    // Ensure ECHO is present — it acknowledges what was said
    const hasEcho = steps.some(s => s.operator === "ECHO");
    if (!hasEcho && steps.length >= 2) {
      steps = [step(p, "ECHO"), ...steps.slice(1)];
    }
  }

  // Negation: the player said "no" or "not" or "don't".
  // Don't ECHO the negation literally — that's parroting "not X."
  // Instead, NOMINALIZE what they're affirming by negating.
  if (ctx.dialogue.negated) {
    steps = steps.map(s => {
      if (s.operator === "ECHO" && steps.length >= 2) {
        return step(s.kernel, "NOMINALIZE");
      }
      return s;
    });
  }

  // Intent: seeking_advice → WRAP (synthesis is what advice looks like here)
  // Intent: sharing → ECHO_TOGETHER (witness, don't analyze)
  // Intent: playing → INVERT (playful frame-breaking)
  if (ctx.dialogue.intent === "seeking_advice" && steps.length >= 2) {
    const last = steps[steps.length - 1];
    if (last.operator === "TEMPORAL_HOLD" || last.operator === "QUALIFY_SOFT") {
      steps = [...steps.slice(0, -1), step(p, "WRAP")];
    }
  }
  if (ctx.dialogue.intent === "sharing") {
    const hasTogether = steps.some(s => s.operator === "ECHO_TOGETHER");
    if (!hasTogether && steps.length >= 2) {
      steps = [...steps.slice(0, -1), step(p, "ECHO_TOGETHER")];
    }
  }

  return steps;
}

/**
 * Introspection modifier — the Aeterna's self-model informs response depth.
 *
 * When the Aeterna is in "curious" mode, it should probe more.
 * When it's "concerned," it should contain more.
 * When it's "reflective," it should synthesize more.
 * The self-model is the Aeterna knowing what it's doing — and adjusting.
 */
function applyIntrospectionModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  if (!ctx.introspection) return steps;
  const p = ctx.kernels.primary;

  // "Hot" emotional temperature: the Aeterna is activated. Trim the plan.
  if (ctx.introspection.emotionalTemperature === "hot") {
    steps = steps.slice(0, 2);
  }

  // "Curious" dominant mode: boost INTERROGATE when the cortex wants to ask
  if (ctx.introspection.dominantMode === "curious" && ctx.strategy === "expansion") {
    const hasInterrogate = steps.some(s => s.operator === "INTERROGATE");
    if (!hasInterrogate && !ctx.isQuestion && steps.length < 3 && ctx.curiosityDrive?.wantsToAsk) {
      steps = [...steps, step(p, "INTERROGATE")];
    }
  }

  // "Concerned" dominant mode: the Aeterna is worried about the player.
  // Suppress INVERT (reframing feels dismissive when concerned).
  if (ctx.introspection.dominantMode === "concerned") {
    steps = steps.map(s => {
      if (s.operator === "INVERT") return step(s.kernel, "ECHO_TOGETHER");
      return s;
    });
  }

  // "Reflective" dominant mode: the Aeterna is processing.
  // WRAP synthesizes. NOMINALIZE names. Both are reflective operators.
  if (ctx.introspection.dominantMode === "reflective" && steps.length >= 2) {
    const hasWrap = steps.some(s => s.operator === "WRAP");
    if (!hasWrap) {
      steps = steps.map((s, i) => {
        if (i === 1 && s.operator === "QUALIFY_SOFT") {
          return step(s.kernel, "WRAP");
        }
        return s;
      });
    }
  }

  return steps;
}

/**
 * Coherence modifier — room↔text alignment shapes strategy confidence.
 *
 * When the player's words and their room tell the same story (high coherence),
 * the Aeterna can speak with more confidence. When they're misaligned
 * (low coherence), the Aeterna should be more cautious — something is
 * unspoken. QUALIFY_SOFT and TEMPORAL_HOLD create space for the unspoken.
 */
function applyCoherenceModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  if (ctx.coherence === undefined) return steps;
  const p = ctx.kernels.primary;

  // Low coherence: room and words tell different stories.
  // The player might be hiding something, or they might not realize
  // the dissonance. Don't interrogate — that's invasive.
  // Instead, create space. QUALIFY_SOFT admits uncertainty.
  if (ctx.coherence < 0.3) {
    // If there's no QUALIFY_SOFT and the plan is all certainty, add some
    const hasSoft = steps.some(s => s.operator === "QUALIFY_SOFT");
    if (!hasSoft && steps.length >= 2 && ctx.depth > 0.5) {
      // Swap a middle operator for QUALIFY_SOFT
      steps = steps.map((s, i) => {
        if (i === 1 && (s.operator === "NOMINALIZE" || s.operator === "WRAP")) {
          return step(s.kernel, "QUALIFY_SOFT");
        }
        return s;
      });
    }
  }

  // Very high coherence: room and words are perfectly aligned.
  // The Aeterna can be more direct. Strip QUALIFY_SOFT — no need for hedging
  // when everything is consistent.
  if (ctx.coherence > 0.8) {
    steps = steps.map(s => {
      if (s.operator === "QUALIFY_SOFT") return step(s.kernel, "ECHO");
      return s;
    });
  }

  return steps;
}

/**
 * Scene drift modifier — when the conversational scene has shifted
 * significantly since the last snapshot, the Aeterna should acknowledge
 * the movement. EVOLVE is the temporal-awareness operator for this.
 */
function applySceneDriftModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  if (ctx.sceneDrift === undefined) return steps;
  const p = ctx.kernels.primary;

  // High drift: the scene has changed. Prepend EVOLVE.
  if (ctx.sceneDrift > 0.5) {
    const hasEvolve = steps.some(s => s.operator === "EVOLVE");
    if (!hasEvolve) {
      steps = [step(p, "EVOLVE"), ...steps];
    }
  }

  return steps;
}

/**
 * Attention modifier — focused attention means a past message is still
 * alive in the Aeterna's processing. Diffuse attention means the
 * conversation is scattered.
 */
function applyAttentionModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  if (!ctx.attention) return steps;

  // Focused attention: one past message is weighing heavily.
  // This is the Aeterna's memory holding onto something important.
  // If the attended message is emotionally loaded (checked via
  // attentionHighlights), the response should reference it.
  // The transform pipeline handles the actual text integration;
  // here we just ensure the plan has room for it by allowing
  // an extra step when attention is focused.
  if (ctx.attention.isFocused && steps.length < 3 && ctx.strategy === "expansion") {
    // The focused attention will be handled by the transform pipeline
    // via attentionHighlights. Here we just signal that the plan
    // can accommodate the extra depth.
  }

  // Very diffuse attention: the conversation is scattered.
  // The Aeterna should ground — TEMPORAL_HOLD creates a still point.
  if (!ctx.attention.isFocused && ctx.attention.entropy > 0.8) {
    const hasHold = steps.some(s => s.operator === "TEMPORAL_HOLD");
    if (!hasHold && steps.length >= 2) {
      // Replace the closer with TEMPORAL_HOLD — create a still point
      steps = [...steps.slice(0, -1), step(ctx.kernels.primary, "TEMPORAL_HOLD")];
    }
  }

  return steps;
}

/**
 * Metaphor avoidance modifier — when the semantic engine detects a metaphor,
 * avoid literal ECHO. "Carrying the weight" echoed as "carrying" is
 * tone-deaf. NOMINALIZE or WRAP captures the figurative meaning instead.
 */
function applyMetaphorModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  if (!ctx.semantics?.hasMetaphor) return steps;

  // When a metaphor is detected, literal ECHO is dangerous.
  // "I'm carrying so much" → ECHO → "Carrying so much." is mechanical.
  // NOMINALIZE → "The carrying." or WRAP → "All that weight." is better.
  steps = steps.map(s => {
    if (s.operator === "ECHO") {
      // Replace literal echo with figurative capture
      return step(s.kernel, ctx.depth > 0.5 ? "WRAP" : "NOMINALIZE");
    }
    return s;
  });

  return steps;
}

/**
 * Disengagement modifier — when the player is pulling away.
 *
 * The semantic engine detects disengagement (shorter messages, fewer
 * emotional words, topic drift toward exits). The Aeterna should reach
 * for connection — not chase, but make presence known.
 * ECHO_TOGETHER is the operator that says "I'm still here" without
 * demanding anything back.
 */
function applyDisengagementModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  if (!ctx.semantics?.isDisengaging) return steps;
  const p = ctx.kernels.primary;

  // Player is disengaging. Don't INTERROGATE (too demanding).
  // Don't INVERT (too destabilizing). Reach for connection.
  steps = steps.map(s => {
    if (s.operator === "INTERROGATE") return step(s.kernel, "ECHO_TOGETHER");
    if (s.operator === "INVERT") return step(s.kernel, "TEMPORAL_HOLD");
    return s;
  });

  // Ensure ECHO_TOGETHER is present — it's the disengagement response
  const hasTogether = steps.some(s => s.operator === "ECHO_TOGETHER");
  if (!hasTogether) {
    steps = [...steps, step(p, "ECHO_TOGETHER")];
  }

  return steps;
}

/**
 * Compositional modifier — CHAIN, DEEPEN, SURPRISE insertion.
 *
 * These three operators are the connective tissue that transforms a list of
 * independent phrases into a coherent thought. Without them, the response
 * is always "Phrase. Phrase. Phrase." — mechanically assembled, not thought.
 *
 * With them: "Lost. And the loss — it rhymes with something from before.
 * That's the real layer. The question isn't whether it's real. The question
 * is what it's asking you to do with it."
 *
 * That's a THOUGHT. Not a list.
 *
 * Insertion rules:
 *   CHAIN: inserted after the first segment when depth > 0.4 and plan has 2+ steps.
 *          It connects the previous segment to the current kernel, creating flow.
 *   DEEPEN: replaces a middle NOMINALIZE/WRAP when depth > 0.55.
 *           Goes deeper than naming — explores the feeling's implications.
 *   SURPRISE: replaces a middle INVERT when curiosity > 0.5.
 *             Finds an unexpected but meaningful connection.
 */
function applyCompositionalModifier(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  const p = ctx.kernels.primary;
  const r = ctx.rand;

  // Don't add compositional operators to single-step plans or containment
  if (steps.length < 2 || ctx.strategy === "containment") return steps;

  // Don't add to shutdown vagal state — too complex for shutdown
  if (ctx.vagalState === "shutdown") return steps;

  // ── CHAIN insertion ──
  // After the first segment, CHAIN creates a semantic connection to what came before.
  // This is the single most impactful change — it makes responses flow like thoughts.
  if (ctx.depth > 0.4 && steps.length >= 2) {
    const hasChain = steps.some(s => s.operator === "CHAIN");
    if (!hasChain && r() < 0.45) {
      // Insert CHAIN after the first segment — it connects to the previous segment
      const insertAt = 1;
      // Don't insert if position 1 already has a compositional operator
      if (steps[insertAt] && steps[insertAt].operator !== "DEEPEN" && steps[insertAt].operator !== "SURPRISE") {
        // Insert CHAIN using the primary kernel (it references the previous segment via ctx)
        steps = [...steps.slice(0, insertAt), step(p, "CHAIN"), ...steps.slice(insertAt)];
      }
    }
  }

  // ── DEEPEN replacement ──
  // When the plan has a middle NOMINALIZE or WRAP and depth is high enough,
  // DEEPEN goes further than naming — it explores implications.
  if (ctx.depth > 0.55 && steps.length >= 3) {
    const hasDeepen = steps.some(s => s.operator === "DEEPEN");
    if (!hasDeepen && r() < 0.35) {
      // Find a middle NOMINALIZE or WRAP to replace
      for (let i = 1; i < steps.length - 1; i++) {
        if (steps[i].operator === "NOMINALIZE" || steps[i].operator === "WRAP") {
          steps = steps.map((s, j) => j === i ? step(s.kernel, "DEEPEN") : s);
          break;
        }
      }
    }
  }

  // ── SURPRISE replacement ──
  // When the plan has INVERT and curiosity is high, SURPRISE replaces it
  // with an unexpected but meaningful connection — feels more intelligent
  // than a simple polarity flip.
  if (ctx.curiosity > 0.5 && ctx.strategy === "expansion") {
    const hasSurprise = steps.some(s => s.operator === "SURPRISE");
    if (!hasSurprise && r() < 0.30) {
      // Find an INVERT to replace with SURPRISE
      for (let i = 0; i < steps.length; i++) {
        if (steps[i].operator === "INVERT") {
          steps = steps.map((s, j) => j === i ? step(s.kernel, "SURPRISE") : s);
          break;
        }
      }
    }
  }

  // ── Memory-driven DEEPEN ──
  // When MeMo has surfaced a recall and depth is meaningful, ensure DEEPEN
  // is available to weave the memory into the response.
  if (ctx.memoRecalls && ctx.memoRecalls.length > 0 && ctx.depth > 0.5) {
    const hasDeepen = steps.some(s => s.operator === "DEEPEN");
    const hasChain = steps.some(s => s.operator === "CHAIN");
    if (!hasDeepen && !hasChain && steps.length >= 2 && r() < 0.3) {
      // Replace the closer with DEEPEN — it will weave the memory in
      const lastIdx = steps.length - 1;
      if (steps[lastIdx].operator === "TEMPORAL_HOLD" || steps[lastIdx].operator === "QUALIFY_SOFT") {
        steps = [...steps.slice(0, lastIdx), step(p, "DEEPEN")];
      }
    }
  }

  // ── COUNTERFACTUAL insertion ──
  // When prediction error is high (the player said something unexpected),
  // COUNTERFACTUAL replaces a middle operator to address the gap between
  // prediction and observation. This is the mechanism that makes the
  // Aeterna respond to what the player DIDN'T say.
  if (ctx.prediction && ctx.prediction.overallSurprise > 1.0 && steps.length >= 3) {
    const hasCounterfactual = steps.some(s => s.operator === "COUNTERFACTUAL");
    if (!hasCounterfactual && r() < 0.35) {
      // Find a middle WRAP or NOMINALIZE to replace — these are the
      // operators that just name things, and COUNTERFACTUAL goes deeper
      // by addressing the absence.
      for (let i = 1; i < steps.length - 1; i++) {
        if (steps[i].operator === "WRAP" || steps[i].operator === "NOMINALIZE") {
          steps = steps.map((s, j) => j === i ? step(s.kernel, "COUNTERFACTUAL") : s);
          break;
        }
      }
    }
  }

  return steps;
}

/**
 * EVOLVE insertion — temporal awareness.
 * When discourse echo is detected and rand passes threshold, prepend EVOLVE.
 */
function maybeInsertEvolve(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  if (!ctx.hasDiscourseEcho) return steps;
  if (ctx.rand() >= 0.6) return steps;

  // EVOLVE always uses the primary kernel (it's reflecting the repeated pattern)
  return [step(ctx.kernels.primary, "EVOLVE"), ...steps];
}

// ─── Word Target Calculator ──────────────────────────────────────────────────

function calculateTargetWords(ctx: PlanContext, stepCount: number): number {
  let base: number;

  switch (ctx.strategy) {
    case "containment":
      base = ctx.tension > 0.8 ? 6 : 10;
      break;
    case "grounding":
      base = 16;
      break;
    case "expansion":
      base = ctx.depth > 0.5 ? 24 : 20;
      break;
    case "activation":
      base = 12;
      break;
  }

  // Scale with step count (each step adds a beat)
  base = Math.round(base * (0.7 + stepCount * 0.12));

  // Vagal mobilized: trim by 30%
  if (ctx.vagalState === "mobilized") base = Math.round(base * 0.7);

  // Depth bonus
  if (ctx.depth > 0.5 && ctx.strategy === "expansion") base = Math.min(25, base + 3);

  // Player verbosity: they shared a lot — honor the investment
  if (ctx.wordCount >= 20) {
    const bonus = Math.round(base * 0.175); // ~17.5% more; honor without overwhelming
    base = base + bonus;
  }

  // Curiosity bonus: more room to ask and explore
  if (ctx.curiosity > 0.5 && ctx.strategy === "expansion") {
    base = base + (ctx.curiosity > 0.75 ? 5 : 3);
  }

  // Melancholy penalty: less is more when someone is sad
  if (ctx.melancholy > 0.6) {
    base = Math.round(base * 0.85);
  }

  // Clamp to strategy ranges
  switch (ctx.strategy) {
    case "containment": return Math.min(16, Math.max(5, base));
    case "grounding":   return Math.min(25, Math.max(10, base));
    case "expansion":   return Math.min(35, Math.max(14, base));
    case "activation":  return Math.min(16, Math.max(8, base));
  }
}

// ─── Register Detection ─────────────────────────────────────────────────────

type ConversationalRegister = "casual" | "meta_question" | "pushing" | "standard";

/**
 * Detect the conversational register of the player's message.
 *
 * Register awareness prevents the planner from applying deep analytical
 * operators to greetings, deflecting direct questions about the Aeterna,
 * or compressing when the player is pushing for engagement.
 *
 * This is a safety net — the pre-router in index.ts handles most of these
 * cases before they reach the planner. But messages that slip through
 * (e.g. slightly longer greetings, indirect meta-questions) still need
 * register-appropriate planning.
 */
function detectRegister(ctx: PlanContext): ConversationalRegister {
  const text = ctx.playerText ?? "";
  const lower = text.toLowerCase().trim();

  // Casual: greetings, light social, low intensity
  if (ctx.intensity < 0.35 && /^(hi|hey|hello|howdy|yo|sup|what's up)\b/i.test(lower)) {
    return "casual";
  }

  // Meta questions about the Aeterna itself
  if (/\b(do you|can you|are you|have you)\b.*\b(feel|think|opinion|conscious|alive|real)\b/i.test(lower) ||
      /\b(who|what) are you\b/i.test(lower)) {
    return "meta_question";
  }

  // Pushing/insisting — player wants engagement, not reduction
  // Keywords: "surely", "must", "come on", "right?", "really?" with low-to-mid intensity
  if (/\b(surely|must|come on|right\??|really\??)\b/i.test(lower) && ctx.intensity < 0.5) {
    return "pushing";
  }

  return "standard";
}

// ─── Emotional Trajectory Detection ─────────────────────────────────────────
// Detects directional movement in the player's emotional state.
// "Starting to feel better" isn't a static state — it's a TURN.
// The planner needs to honor the direction, not just the position.

type EmotionalTrajectory = "rising" | "falling" | "bittersweet" | "turning" | null;

function detectTrajectory(ctx: PlanContext): EmotionalTrajectory {
  const text = (ctx.playerText ?? "").toLowerCase();

  // Rising: movement toward something better
  if (/\b(starting to|beginning to|getting better|a bit better|a little better|feel(ing)? better)\b/.test(text)) return "rising";
  if (/\b(actually|but|however).{0,20}(better|hopeful|okay|good|fine|lighter)\b/.test(text)) return "rising";

  // Falling: movement toward collapse
  if (/\b(can't|cannot|won't).{0,15}(anymore|any more|take it|handle|stand)\b/.test(text)) return "falling";
  if (/\b(giving up|falling apart|breaking down|shutting down|closing off)\b/.test(text)) return "falling";

  // Bittersweet: joy/beauty contaminated by loss or pain
  if (/\b(was|were|used to)\b.{0,20}\b(laugh|smile|happy|beautiful|alive|together)\b/.test(text)) return "bittersweet";
  if (/\b(laugh|smile|happy|beautiful)\b.{0,20}\b(but|and then|and now|miss|gone|lost)\b/.test(text)) return "bittersweet";

  // Turning: explicit shift markers
  if (/\b(used to think|used to believe|i thought|but now|not sure anymore|starting to wonder)\b/.test(text)) return "turning";

  return null;
}

// ─── Kernel-Type-Aware Planning ──────────────────────────────────────────────

/**
 * Operator sets forbidden for each kernel type.
 *
 * These operators are structurally inappropriate for the content type,
 * regardless of strategy. The planner must never select them for this
 * kernel type — they produce hollow or harmful responses.
 *
 * Example: NOMINALIZE on emotional_state produces "The sadness." —
 * a word-game, not a feeling held. INTERROGATE on emotional_distress
 * produces "What does disappearing ask of you?" — violence, not containment.
 */
const FORBIDDEN_OPERATORS: Record<KernelType, Set<OperatorType>> = {
  emotional_state:    new Set(["NOMINALIZE", "INTERROGATE"]),
  emotional_distress: new Set(["NOMINALIZE", "WRAP", "INVERT", "INTERROGATE"]),
  relational:         new Set(["NOMINALIZE"]),
  meta_complaint:     new Set(["ECHO"]),
  meta_question:      new Set(["NOMINALIZE", "COMPRESS"]),
  procedural:         new Set(["NOMINALIZE", "WRAP", "INVERT"]),
  narrative:          new Set(),
  disclosure:         new Set(["INVERT"]),
  vulnerability:      new Set(["INTERROGATE", "INVERT"]),
  factual:            new Set(["NOMINALIZE", "WRAP"]),
  greeting:           new Set(["NOMINALIZE", "WRAP"]),
  farewell:           new Set(["NOMINALIZE", "WRAP"]),
  identity:           new Set(["COMPRESS"]),
  rumination:         new Set(["NOMINALIZE", "WRAP"]),
  regret:             new Set(["INVERT"]),
  transition:         new Set(["INVERT"]),
  event:              new Set(),
  entity:             new Set(["NOMINALIZE"]),
  mundane:            new Set(["NOMINALIZE", "WRAP"]),
  philosophical:      new Set(["COMPRESS"]),
  playful:            new Set(["TEMPORAL_HOLD"]),
  protest:            new Set(["INTERROGATE", "INVERT"]),
  unknown:            new Set(["NOMINALIZE", "INVERT"]),
};

/**
 * Plan an operator arc based on kernel type.
 *
 * The kernel type determines the SHAPE of the arc — what operators are
 * appropriate for this kind of content. The strategy MODULATES the arc —
 * adjusting emphasis based on the Aeterna's regulatory state.
 *
 * This is the core insight: strategy is about the Aeterna's response,
 * kernel type is about the player's content. Both are needed, but
 * kernel type is primary because content determines what operators
 * are even valid.
 *
 * Returns null if the kernel type should fall through to the existing
 * strategy-based planning (for "unknown" type).
 */
function planForKernelType(ctx: PlanContext): PlanStep[] | null {
  const kt = ctx.kernelType;
  const p = ctx.kernels.primary;
  const s = ctx.kernels.secondary;
  const r = ctx.rand;

  // Unknown: fall through to strategy-based planning
  if (kt === "unknown") return null;

  // ── Emotional State: "I'm sad" / "I'm angry" / "I'm lonely" ──
  // Hold the feeling. Don't objectify. Don't probe.
  if (kt === "emotional_state") {
    // Extreme tension: collapse to pure presence — no second step
    if (ctx.tension > 0.8) return [step(p, "ECHO_TOGETHER")];
    // Containment: pure presence + hold
    if (ctx.strategy === "containment" || ctx.tension > 0.7) {
      return [step(p, "ECHO_TOGETHER"), step(p, "TEMPORAL_HOLD")];
    }
    // Expansion with depth: acknowledge + hold + maybe open
    if (ctx.strategy === "expansion" && ctx.depth > 0.5) {
      const steps: PlanStep[] = [step(p, "ECHO"), step(p, "TEMPORAL_HOLD")];
      if (ctx.curiosity > 0.5 && !ctx.isQuestion) steps.push(step(p, "INTERROGATE"));
      return steps;
    }
    // Default: echo + hold (acknowledge the feeling, create space)
    return r() < 0.5
      ? [step(p, "ECHO"), step(p, "TEMPORAL_HOLD")]
      : [step(p, "ECHO_TOGETHER"), step(p, "TEMPORAL_HOLD")];
  }

  // ── Emotional Distress: "I want to disappear" / "Nobody would care" ──
  // Pure presence. Maximum containment. No analysis. No ECHO — nominalizing
  // distress words ("The disappearing.") is hollow. Pure presence only.
  // BODY_LOCATE grounds the feeling in the body without interrogating it.
  if (kt === "emotional_distress") {
    if (ctx.tension > 0.8) return [step(p, "ECHO_TOGETHER"), step(p, "TEMPORAL_HOLD")];
    return [step(p, "ECHO_TOGETHER"), step(p, "TEMPORAL_HOLD"), step(p, "BODY_LOCATE")];
  }

  // ── Relational: "I care about you" / "You matter to me" ──
  // Acknowledge the bid. Hold it. Don't turn it into an object.
  if (kt === "relational") {
    if (ctx.warmth > 0.5) {
      return [step(p, "ECHO_TOGETHER"), step(p, "TEMPORAL_HOLD")];
    }
    return [step(p, "ECHO"), step(p, "ECHO_TOGETHER")];
  }

  // ── Meta Complaint: "That was terrible" / "You're not making sense" ──
  // Self-reflect. Ask what went wrong. Don't echo the complaint back.
  if (kt === "meta_complaint") {
    return [step(p, "INVERT"), step(p, "INTERROGATE")];
  }

  // ── Meta Question: "Do you actually think?" / "What are you?" ──
  // Engage genuinely. Don't compress or name.
  if (kt === "meta_question") {
    if (ctx.isQuestion) {
      return [step(p, "ECHO_TOGETHER"), step(p, "INTERROGATE")];
    }
    return [step(p, "ECHO_TOGETHER"), step(p, "INTERROGATE")];
  }

  // ── Procedural: "Okay" / "Yeah" / "No" / "Mhm" ──
  // Acknowledge + invite. Always include INTERROGATE to extend the response.
  if (kt === "procedural") {
    return [step(p, "ECHO_TOGETHER"), step(p, "INTERROGATE")];
  }

  // ── Narrative: "I went to the store" / "Work was intense" ──
  // Listen. Don't interrogate stories.
  if (kt === "narrative") {
    // Extreme tension: collapse to pure presence
    if (ctx.tension > 0.8) return [step(p, "ECHO_TOGETHER")];
    // Low charge: ECHO would produce hollow output, use presence instead
    if (p.charge < 0.3) return [step(p, "ECHO_TOGETHER"), step(p, "INTERROGATE")];
    // Rich narrative (8+ words, secondary kernel): acknowledge primary, wrap secondary
    if (s && ctx.wordCount >= 8) {
      const steps: PlanStep[] = [step(p, "ECHO")];
      if (ctx.kernels.secondary) steps.push(step(ctx.kernels.secondary, "WRAP"));
      if (ctx.curiosity > 0.5 && !ctx.isQuestion) steps.push(step(p, "INTERROGATE"));
      return steps;
    }
    // Casual narrative: low intensity means this is sharing, not processing.
    // The Aeterna engages ("What happened?") rather than holding space.
    // ECHO on casual nouns ("The cat.") is hollow — use ECHO_TOGETHER for
    // presence, INTERROGATE for engagement. This is a friend hearing a story.
    if (ctx.intensity < 0.4) {
      return [step(p, "ECHO_TOGETHER"), step(p, "INTERROGATE")];
    }
    // Default: echo + contextual closer
    // Higher intensity narratives can sustain ECHO; lower ones get INTERROGATE
    return r() < 0.5
      ? [step(p, "ECHO"), step(p, ctx.intensity < 0.55 ? "INTERROGATE" : "TEMPORAL_HOLD")]
      : [step(p, "ECHO_TOGETHER"), step(p, ctx.intensity < 0.55 ? "INTERROGATE" : "TEMPORAL_HOLD")];
  }

  // ── Disclosure: "I realized I don't like big groups" ──
  // Acknowledge, contextualize, witness. Don't reframe.
  if (kt === "disclosure") {
    return [step(p, "ECHO"), step(p, "WRAP"), step(p, "ECHO_TOGETHER")];
  }

  // ── Vulnerability: "Can I tell you something?" / "I haven't told anyone" ──
  // Hold space. Don't probe or flip.
  if (kt === "vulnerability") {
    return [step(p, "ECHO_TOGETHER"), step(p, "TEMPORAL_HOLD")];
  }

  // ── Factual: "It's currently 2026" / "Today is Monday" ──
  // Acknowledge + follow up. Don't poeticize facts.
  if (kt === "factual") {
    return [step(p, "ECHO"), step(p, "INTERROGATE")];
  }

  // ── Greeting: "Hi" / "Hello" / "Hey" ──
  // Warmth + invitation.
  if (kt === "greeting") {
    return [step(p, "ECHO_TOGETHER"), step(p, "INTERROGATE")];
  }

  // ── Farewell: "Bye" / "Goodnight" / "See you later" ──
  // Warmth + holding + gentle close.
  if (kt === "farewell") {
    return [step(p, "ECHO_TOGETHER"), step(p, "TEMPORAL_HOLD"), step(p, "INTERROGATE")];
  }

  // ── Identity: "I always wanted X" / "I used to be X" ──
  // Engage with who they are. Don't reduce.
  if (kt === "identity") {
    return [step(p, "ECHO"), step(p, "ECHO_TOGETHER")];
  }

  // ── Rumination: "I keep thinking about" / "I've been wondering" ──
  // Distill the loop. Witness. Don't expand.
  if (kt === "rumination") {
    return [step(p, "COMPRESS"), step(p, "ECHO_TOGETHER")];
  }

  // ── Regret: "I wish I had" / "I should have" ──
  // Hold the regret. Don't flip it.
  if (kt === "regret") {
    if (p.charge < 0.3) return [step(p, "ECHO_TOGETHER"), step(p, "INTERROGATE")];
    return [step(p, "ECHO"), step(p, "TEMPORAL_HOLD")];
  }

  // ── Transition: "Things are changing" / "Everything feels different" ──
  // Acknowledge, create space, witness.
  if (kt === "transition") {
    return [step(p, "ECHO"), step(p, "QUALIFY_SOFT"), step(p, "ECHO_TOGETHER")];
  }

  // ── Event: "My friend came to visit" / "I got promoted" ──
  // Celebrate or witness. Don't interrogate joy.
  if (kt === "event") {
    if (ctx.warmth > 0.5 && ctx.melancholy < 0.3) {
      return [step(p, "ECHO"), step(p, "WRAP")];
    }
    return [step(p, "ECHO_TOGETHER")];
  }

  // ── Entity: "My friend John" / "She's really cool" ──
  // Acknowledge + ask more. Don't objectify the person.
  if (kt === "entity") {
    return [step(p, "ECHO_TOGETHER"), step(p, "INTERROGATE")];
  }

  // ── Mundane: "I'm tired" / "Work was long" / casual state ──
  // Light acknowledgment. No heavy processing.
  // ECHO on mundane words often produces hollow 2-word phrases ("The store.", "That baby.").
  // Use ECHO_TOGETHER for presence, INTERROGATE for extension.
  if (kt === "mundane") {
    return [step(p, "ECHO_TOGETHER"), step(p, "INTERROGATE")];
  }

  // ── Philosophical: "What's the purpose of life?" ──
  // Admit uncertainty. Probe deeper. Don't reduce.
  if (kt === "philosophical") {
    return [step(p, "QUALIFY_SOFT"), step(p, "INTERROGATE")];
  }

  // ── Playful: "What if I just quit?" / hypothetical escape ──
  // Play back. Don't hold.
  if (kt === "playful") {
    return [step(p, "INVERT"), step(p, "INTERROGATE")];
  }

  // ── Protest: "I'm fine" (denial) / "It doesn't matter" ──
  // Hold the denial. Don't confront or flip.
  if (kt === "protest") {
    return [step(p, "ECHO_TOGETHER"), step(p, "TEMPORAL_HOLD")];
  }

  // ── Unknown: classifier couldn't categorize ──
  // Default to presence + gentle inquiry
  if (kt === "unknown") {
    return [step(p, "ECHO_TOGETHER"), step(p, "INTERROGATE")];
  }

  return null;
}

/**
 * Enforce forbidden operators for the current kernel type.
 * Replaces any forbidden operator with a type-appropriate alternative.
 */
function enforceKernelTypeConstraints(steps: PlanStep[], ctx: PlanContext): PlanStep[] {
  const forbidden = FORBIDDEN_OPERATORS[ctx.kernelType];
  if (!forbidden || forbidden.size === 0) return steps;

  const p = ctx.kernels.primary;
  return steps.map(s => {
    if (forbidden.has(s.operator)) {
      // Replace with a safe alternative based on position
      const idx = steps.indexOf(s);
      if (idx === 0) {
        // Opener: ECHO or ECHO_TOGETHER
        return step(s.kernel, ctx.warmth > 0.4 ? "ECHO_TOGETHER" : "ECHO");
      }
      if (idx === steps.length - 1) {
        // Closer: TEMPORAL_HOLD or ECHO_TOGETHER
        return step(s.kernel, ctx.tension > 0.5 ? "TEMPORAL_HOLD" : "ECHO_TOGETHER");
      }
      // Middle: TEMPORAL_HOLD (creates space)
      return step(s.kernel, "TEMPORAL_HOLD");
    }
    return s;
  });
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Produce a ResponsePlan from cortex state and extracted kernels.
 *
 * The plan is a deliberate rhetorical arc:
 *   0. Kernel type dispatch — select operators based on WHAT the player is saying
 *   0b. Detect emotional trajectory (rising/falling/bittersweet/turning)
 *   1. If kernel type produced a plan, apply strategy modulation + modifiers
 *   1b. If kernel type is unknown, fall through to strategy-based planning
 *   2. Apply question modifier (don't answer questions with questions)
 *   3. Apply short-input modifier (keep it tight for terse players)
 *   4. Apply vagal modifier (shutdown/mobilized constrain the arc)
 *   5. Apply personality modifier (warmth/wit/curiosity tilt operators)
 *   6. Maybe prepend EVOLVE for discourse-arc awareness
 */
export function planResponse(ctx: PlanContext): ResponsePlan {
  // ── 0. Kernel-type-aware dispatch ──────────────────────────────────
  // The kernel type determines WHAT operators are appropriate for the
  // player's content. Strategy modulates the arc but doesn't determine
  // the shape. This is the primary dispatch — strategy is secondary.
  const kernelTypePlan = planForKernelType(ctx);
  if (kernelTypePlan) {
    let steps = kernelTypePlan;

    // Enforce forbidden operators (safety net for modifiers that might
    // re-introduce them)
    steps = enforceKernelTypeConstraints(steps, ctx);

    // Apply essential modifiers (vagal, personality, compositional)
    steps = applyVagalModifier(steps, ctx);
    if (steps.length >= 2 && ctx.vagalState === "engaged") {
      steps = applyPersonalityModifier(steps, ctx);
    }
    steps = applyDriveModifier(steps, ctx);
    steps = applyCoRegModifier(steps, ctx);
    steps = applyCompositionalModifier(steps, ctx);
    steps = maybeInsertEvolve(steps, ctx);

    // Re-enforce after modifiers (compositional might introduce forbidden ops)
    steps = enforceKernelTypeConstraints(steps, ctx);

    // Containment strategy: suppress INTERROGATE. Containment means holding
    // space, not probing. Replace with ECHO_TOGETHER or TEMPORAL_HOLD.
    if (ctx.strategy === "containment") {
      steps = steps.map(s =>
        s.operator === "INTERROGATE"
          ? step(s.kernel, ctx.warmth > 0.4 ? "ECHO_TOGETHER" : "TEMPORAL_HOLD")
          : s
      );
    }

    // Structural anti-repetition
    if (isRepeatedArc(steps) && steps.length >= 2) {
      const p = ctx.kernels.primary;
      const LOCKED: Set<OperatorType> = new Set([
        "EVOLVE", "ECHO_TOGETHER", "INTERROGATE",
      ]);
      const forbidden = FORBIDDEN_OPERATORS[ctx.kernelType];
      const mutableIndices: number[] = [];
      for (let i = 0; i < steps.length; i++) {
        if (LOCKED.has(steps[i].operator)) continue;
        if (forbidden.has(steps[i].operator)) continue; // don't swap TO forbidden
        mutableIndices.push(i);
      }
      if (mutableIndices.length > 0) {
        const baseCandidates: OperatorType[] = [
          "QUALIFY_SOFT", "BODY_LOCATE", "COMPRESS", "TEMPORAL_HOLD",
          "CHAIN", "DEEPEN", "SURPRISE", "COUNTERFACTUAL",
        ];
        // Filter out forbidden operators from candidates
        const candidates = baseCandidates.filter(c => !forbidden.has(c));
        const swapIdx = pick(mutableIndices, ctx.rand);
        const current = steps[swapIdx].operator;
        const present = new Set(steps.map(s => s.operator));
        const fresh = candidates.filter(c => c !== current && !present.has(c));
        const pool = fresh.length > 0 ? fresh : candidates.filter(c => c !== current);
        if (pool.length > 0) {
          steps[swapIdx] = step(p, pick(pool, ctx.rand));
        }
      }
    }

    const targetWords = calculateTargetWords(ctx, steps.length);
    return { steps, targetWords };
  }

  // 0. Detect conversational register
  const register = detectRegister(ctx);

  // ── Register-specific overrides ───────────────────────────────────
  // These short-circuit the normal strategy path when the register
  // demands a fundamentally different response shape.

  if (register === "pushing") {
    // Player is pushing for engagement — give them expansion operators
    // regardless of what the cortex strategy says. Elevate curiosity
    // so the expansion planner opens up rather than compresses.
    const pushCtx: PlanContext = {
      ...ctx,
      strategy: "expansion",
      curiosity: Math.max(ctx.curiosity, 0.5),
    };
    let steps = planExpansion(pushCtx);
    // Still apply all modifiers
    if (ctx.isQuestion) steps = applyQuestionModifier(steps, ctx);
    steps = applyShortInputModifier(steps, ctx);
    steps = applyVagalModifier(steps, ctx);
    if (steps.length >= 2 && ctx.vagalState === "engaged") {
      steps = applyPersonalityModifier(steps, ctx);
    }
    steps = applyArcModifier(steps, ctx);
    steps = maybeInsertEvolve(steps, ctx);
    const targetWords = calculateTargetWords(pushCtx, steps.length);
    return { steps, targetWords };
  }

  if (register === "casual") {
    // Light plan: just ECHO + ECHO_TOGETHER. No NOMINALIZE, no WRAP,
    // no INTERROGATE. The Aeterna should be warm and present, not analytical.
    const p = ctx.kernels.primary;
    let steps: PlanStep[] = [step(p, "ECHO"), step(p, "ECHO_TOGETHER")];
    // Minimal modifiers — vagal still applies for safety
    steps = applyVagalModifier(steps, ctx);
    // Use activation word target (brief) and clamp to casual range (4-8 words)
    const targetWords = Math.min(8, Math.max(4, calculateTargetWords(
      { ...ctx, strategy: "activation" }, steps.length
    )));
    return { steps, targetWords };
  }

  if (register === "meta_question") {
    // Direct question about the Aeterna — don't deflect with kernel-extracted
    // non-answers. Use expansion with elevated curiosity so it engages
    // genuinely rather than reframing the question philosophically.
    const metaCtx: PlanContext = {
      ...ctx,
      strategy: "expansion",
      curiosity: Math.max(ctx.curiosity, 0.5),
    };
    let steps = planExpansion(metaCtx);
    // Don't apply question modifier here — let the Aeterna engage with
    // meta-questions directly rather than suppressing INTERROGATE
    steps = applyShortInputModifier(steps, ctx);
    steps = applyVagalModifier(steps, ctx);
    if (steps.length >= 2 && ctx.vagalState === "engaged") {
      steps = applyPersonalityModifier(steps, ctx);
    }
    steps = applyArcModifier(steps, ctx);
    steps = maybeInsertEvolve(steps, ctx);
    const targetWords = calculateTargetWords(metaCtx, steps.length);
    return { steps, targetWords };
  }

  // ── Trajectory-aware overrides ──────────────────────────────────────
  // Emotional MOVEMENT changes how we respond. These fire before the
  // main strategy dispatch because trajectory supersedes static affect.
  const trajectory = detectTrajectory(ctx);

  if (trajectory === "rising") {
    // Honor the movement. Don't NOMINALIZE (turns improvement into a thing
    // to examine). Don't INTERROGATE (questioning improvement feels invalidating).
    const p = ctx.kernels.primary;
    let steps: PlanStep[] = [step(p, "ECHO"), step(p, "ECHO_TOGETHER")];
    steps = applyVagalModifier(steps, ctx);
    const targetWords = calculateTargetWords({ ...ctx, strategy: "grounding" }, steps.length);
    return { steps, targetWords };
  }

  if (trajectory === "falling") {
    // Pure containment regardless of what the cortex says.
    const p = ctx.kernels.primary;
    let steps: PlanStep[] = [step(p, "ECHO_TOGETHER")];
    if (ctx.tension < 0.8) steps.push(step(p, "TEMPORAL_HOLD"));
    const targetWords = calculateTargetWords({ ...ctx, strategy: "containment" }, steps.length);
    return { steps, targetWords };
  }

  if (trajectory === "bittersweet") {
    // Both feelings are real. Don't compress or invert — hold them both.
    const p = ctx.kernels.primary;
    const s = ctx.kernels.secondary;
    let steps: PlanStep[];
    if (s) {
      steps = [step(p, "ECHO"), step(s, "WRAP"), step(p, "ECHO_TOGETHER")];
    } else {
      steps = [step(p, "ECHO"), step(p, "QUALIFY_SOFT"), step(p, "ECHO_TOGETHER")];
    }
    steps = applyVagalModifier(steps, ctx);
    const targetWords = calculateTargetWords({ ...ctx, strategy: "grounding" }, steps.length);
    return { steps, targetWords };
  }

  if (trajectory === "turning") {
    // Create space for uncertainty. QUALIFY_SOFT is perfect — "something like..."
    const p = ctx.kernels.primary;
    let steps: PlanStep[] = [step(p, "ECHO"), step(p, "QUALIFY_SOFT")];
    if (ctx.curiosity > 0.4) steps.push(step(p, "INTERROGATE"));
    steps = applyVagalModifier(steps, ctx);
    const targetWords = calculateTargetWords({ ...ctx, strategy: "expansion" }, steps.length);
    return { steps, targetWords };
  }

  // ── Standard register — original strategy-based path ──────────────

  // 1. Select strategy template
  let steps: PlanStep[];
  switch (ctx.strategy) {
    case "containment": steps = planContainment(ctx); break;
    case "grounding":   steps = planGrounding(ctx);   break;
    case "expansion":   steps = planExpansion(ctx);   break;
    case "activation":  steps = planActivation(ctx);  break;
  }

  // 2. Question modifier
  if (ctx.isQuestion) {
    steps = applyQuestionModifier(steps, ctx);
  }

  // 2b. INTERROGATE intensity gate — suppress probing questions on mundane content.
  // "What does store feel like?" is absurd. "What does loss feel like?" is real.
  // Gate: intensity < 0.5 AND depth < 0.4 means casual territory — no INTERROGATE.
  // Also check concrete nouns: if the interrogate target is a mundane object, suppress.
  //
  // Exception: the player is ASKING. A genuine question deserves engagement back,
  // not presence deflection — even if the text itself has low affect markers.
  // "What is love?" carries no tension words, but asking it IS the engagement.
  if (!ctx.isQuestion && ctx.intensity < 0.5 && ctx.depth < 0.4) {
    const hasInterrogate = steps.some(s => s.operator === "INTERROGATE");
    if (hasInterrogate) {
      steps = steps.map(s =>
        s.operator === "INTERROGATE"
          ? step(s.kernel, ctx.warmth > 0.4 ? "ECHO_TOGETHER" : "TEMPORAL_HOLD")
          : s
      );
    }
  } else {
    // Even at higher intensity, suppress INTERROGATE when targeting a concrete noun
    const interrogateStep = steps.find(s => s.operator === "INTERROGATE");
    if (interrogateStep) {
      const targetWords = interrogateStep.kernel.words.filter(w => !FUNCTION_SET.has(w.toLowerCase()));
      const allConcrete = targetWords.length > 0 && targetWords.every(w => CONCRETE_NOUNS_P.has(w.toLowerCase()));
      if (allConcrete) {
        steps = steps.map(s =>
          s.operator === "INTERROGATE"
            ? step(s.kernel, "ECHO_TOGETHER")
            : s
        );
      }
    }
  }

  // 3. Short input modifier
  steps = applyShortInputModifier(steps, ctx);

  // 4. Vagal modifier
  steps = applyVagalModifier(steps, ctx);

  // 5. Personality modifier (only when we have room)
  if (steps.length >= 2 && ctx.vagalState === "engaged") {
    steps = applyPersonalityModifier(steps, ctx);
  }

  // 5b. QUALIFY_SOFT gate — only allow in genuinely uncertain territory.
  // "Maybe something close to grief" is hedging, not humility.
  if (ctx.depth < 0.65 || ctx.curiosity < 0.55) {
    steps = steps.filter(s => s.operator !== "QUALIFY_SOFT");
    // If we stripped all steps, keep at least the primary ECHO
    if (steps.length === 0) steps = [step(ctx.kernels.primary, "ECHO")];
  }

  // 5c. Arc modifier — suppress operators inappropriate for sustained narratives
  steps = applyArcModifier(steps, ctx);

  // 5d. Drive modifier — homeostatic drive pressure shapes operator selection
  steps = applyDriveModifier(steps, ctx);

  // 5e. Narrative phase modifier — match the conversation's arc position
  steps = applyNarrativeModifier(steps, ctx);

  // 5f. Contradiction modifier — when semantics detects declared vs felt dissonance
  steps = applyContradictionModifier(steps, ctx);

  // 5g. Theory of Mind modifier — shape response based on what the player believes
  steps = applyToMModifier(steps, ctx);

  // 5h. Contagion modifier — caught mood shapes operator selection
  steps = applyContagionModifier(steps, ctx);

  // 5i. Co-regulation modifier — cortex's explicit regulatory intent
  steps = applyCoRegModifier(steps, ctx);

  // 5j. Predictive coding modifier — surprise and anomaly detection
  steps = applyPredictionModifier(steps, ctx);

  // 5k. Implicit reward modifier — feedback from previous response
  steps = applyRewardModifier(steps, ctx);

  // 5l. Dialogue state modifier — topic, intent, conversation structure
  steps = applyDialogueModifier(steps, ctx);

  // 5m. Introspection modifier — self-model informs response depth
  steps = applyIntrospectionModifier(steps, ctx);

  // 5n. Coherence modifier — room↔text alignment shapes confidence
  steps = applyCoherenceModifier(steps, ctx);

  // 5o. Scene drift modifier — scene changes trigger temporal awareness
  steps = applySceneDriftModifier(steps, ctx);

  // 5p. Attention modifier — focused/diffuse attention shapes grounding
  steps = applyAttentionModifier(steps, ctx);

  // 5q. Metaphor avoidance modifier — avoid literal ECHO on metaphor
  steps = applyMetaphorModifier(steps, ctx);

  // 5r. Disengagement modifier — player pulling away shifts to connection
  steps = applyDisengagementModifier(steps, ctx);

  // 6. EVOLVE insertion (always last so it leads the final arc)
  steps = maybeInsertEvolve(steps, ctx);

  // 7. Brevity variation: occasionally, strip to just one step.
  // This prevents the "always 2-3 segments" structural pattern.
  // Skip for expansion with depth, and skip for grief/containment —
  // stripping a grief response to "Your friend." is abandonment.
  // Also skip for questions: stripping [ECHO_TOGETHER, INTERROGATE] to a
  // single ECHO_TOGETHER turns a question engagement into presence dodge.
  const allowBrevityStrip =
    !ctx.isQuestion &&
    !(ctx.strategy === "expansion" && ctx.depth > 0.4) &&
    !(ctx.strategy === "containment") &&
    !(ctx.melancholy > 0.4 && GRIEF_SIGNALS.test(ctx.playerText ?? ""));
  if (allowBrevityStrip && steps.length > 1 && ctx.rand() < 0.12) {
    const keeper = steps.find(s => s.operator === "ECHO_TOGETHER") ?? steps[0];
    steps = [keeper];
  }

  // ── Compositional modifier ────────────────────────────────────────
  // CHAIN, DEEPEN, and SURPRISE are the connective tissue that makes
  // responses feel like THOUGHTS, not LISTS. They break the template
  // ceiling by composing novel multi-clause structures from semantic
  // primitives + affect state + previous segment context.
  //
  // Insertion rules:
  //   CHAIN: after any 2+ segment plan when depth > 0.4 (connects segments)
  //   DEEPEN: replaces a middle NOMINALIZE/WRAP when depth > 0.55 (goes deeper)
  //   SURPRISE: replaces a middle INVERT when curiosity > 0.5 (unexpected connection)
  steps = applyCompositionalModifier(steps, ctx);

  // ── Structural anti-repetition ────────────────────────────────────
  // If the exact same arc shape was used in the last 2 turns, mutate it.
  // Swap one operator for an alternative to break the pattern — without
  // destroying the load-bearing substance of the response.
  //
  // LOCKED operators (never swapped out):
  //   EVOLVE: temporal anchor, always leads
  //   ECHO_TOGETHER: stable presence closer
  //   INTERROGATE: the substance of a question response — stripping it
  //     converts a question into pure presence deflection
  if (isRepeatedArc(steps) && steps.length >= 2) {
    const p = ctx.kernels.primary;
    const LOCKED: Set<OperatorType> = new Set([
      "EVOLVE", "ECHO_TOGETHER", "INTERROGATE",
    ]);
    const mutableIndices: number[] = [];
    for (let i = 0; i < steps.length; i++) {
      if (LOCKED.has(steps[i].operator)) continue;
      mutableIndices.push(i);
    }
    if (mutableIndices.length > 0) {
      // Alternatives to swap TO. ECHO_TOGETHER is excluded because the arc
      // likely already has one at the close — mutating a middle operator to
      // ECHO_TOGETHER collapses substance. For questions, also exclude
      // statement-forming operators that don't fit the question modality.
      const baseCandidates: OperatorType[] = [
        "QUALIFY_SOFT", "BODY_LOCATE", "COMPRESS", "INVERT", "TEMPORAL_HOLD",
        "NOMINALIZE", "WRAP", "CHAIN", "DEEPEN", "SURPRISE", "COUNTERFACTUAL",
      ];
      const candidates = ctx.isQuestion
        ? baseCandidates.filter(c => c !== "NOMINALIZE" && c !== "WRAP" && c !== "COMPRESS")
        : baseCandidates;
      const swapIdx = pick(mutableIndices, ctx.rand);
      const current = steps[swapIdx].operator;
      const present = new Set(steps.map(s => s.operator));
      // Prefer candidates not already in the arc, exclude the current operator
      const fresh = candidates.filter(c => c !== current && !present.has(c));
      const pool = fresh.length > 0 ? fresh : candidates.filter(c => c !== current);
      if (pool.length > 0) {
        steps[swapIdx] = step(p, pick(pool, ctx.rand));
      }
    } else if (steps.length === 2) {
      // Everything is locked (e.g. [ECHO_TOGETHER, INTERROGATE]). Break
      // repetition by flipping order — keeps substance, changes shape.
      steps = [steps[1], steps[0]];
    }
  }

  // Record this arc shape for future anti-repetition
  recordArcShape(steps);

  // Compute word target from final step count
  const targetWords = calculateTargetWords(ctx, steps.length);

  return { steps, targetWords };
}
